import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Typography } from '../common/Typography'
import { LoadingSpinner } from '~/components/icons'

const SearchIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <path
      d='M14.2125 13.3516L10.1547 9.29375C10.7844 8.47969 11.125 7.48438 11.125 6.4375C11.125 5.18438 10.6359 4.00937 9.75156 3.12344C8.86719 2.2375 7.68906 1.75 6.4375 1.75C5.18594 1.75 4.00781 2.23906 3.12344 3.12344C2.2375 4.00781 1.75 5.18438 1.75 6.4375C1.75 7.68906 2.23906 8.86719 3.12344 9.75156C4.00781 10.6375 5.18438 11.125 6.4375 11.125C7.48438 11.125 8.47813 10.7844 9.29219 10.1562L13.35 14.2125C13.362 14.224 13.376 14.234 13.3916 14.2404C13.4071 14.2468 13.4238 14.2501 13.4406 14.2501C13.4575 14.2501 13.4741 14.2468 13.4897 14.2404C13.5052 14.234 13.5194 14.224 13.5312 14.2125L14.2125 13.5328C14.2244 13.521 14.2338 13.5069 14.2403 13.4913C14.2467 13.4757 14.2501 13.459 14.2501 13.4422C14.2501 13.4254 14.2467 13.4088 14.2403 13.3931C14.2338 13.3776 14.2244 13.3635 14.2125 13.3516ZM8.9125 8.9125C8.25 9.57344 7.37187 9.9375 6.4375 9.9375C5.50312 9.9375 4.625 9.57344 3.9625 8.9125C3.30156 8.25 2.9375 7.37187 2.9375 6.4375C2.9375 5.50312 3.30156 4.62344 3.9625 3.9625C4.625 3.30156 5.50312 2.9375 6.4375 2.9375C7.37187 2.9375 8.25156 3.3 8.9125 3.9625C9.57344 4.625 9.9375 5.50312 9.9375 6.4375C9.9375 7.37187 9.57344 8.25156 8.9125 8.9125Z'
      fill='black'
      fillOpacity='0.45'
    />
  </svg>
)

const CloseIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <path
      d='M7.05588 8L3.28461 4.22873L4.22742 3.28592L7.99869 7.05719L11.77 3.28592L12.7128 4.22873L8.9415 8L12.7127 11.7712L11.7699 12.714L7.99869 8.94281L4.22749 12.714L3.28468 11.7712L7.05588 8Z'
      fill='black'
      fillOpacity='0.45'
    />
  </svg>
)

/** ========== types ========== */
export interface FilterChip {
  id: string
  label: string
  value: string
  category?: string
}
export interface MultiSelectItem {
  id: string
  label: string
  value: string
  category?: string
}

export interface MultiSelectProps {
  items: MultiSelectItem[]
  selectedItems: string[]
  onSelectionChange: (selectedIds: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  showSelectedAsChips?: boolean
  className?: string
  maxHeight?: string
  isLoading?: boolean
  /** Called when the dropdown opens or closes (e.g. for lazy-loading data on focus) */
  onOpenChange?: (isOpen: boolean) => void
}
type SearchRowProps = {
  isOpen: boolean
  isFull: boolean
  filterChips: { id: string; label: string; value: string; category?: string }[]
  showSelectedAsChips: boolean
  searchValue: string
  placeholderText: string
  attachScrollEl: (node: HTMLDivElement | null) => void
  onOpen: () => void
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onRemoveChip: (id: string) => void
}

const SearchRow: React.FC<SearchRowProps> = React.memo(
  ({
    isOpen,
    isFull,
    filterChips,
    showSelectedAsChips,
    searchValue,
    placeholderText,
    attachScrollEl,
    onOpen,
    onSearchChange,
    onKeyDown,
    onRemoveChip
  }) => {
    const baseRow =
      'relative inline-flex w-full items-center gap-1 bg-white rounded-xl pl-2 pr-2 py-1.5 h-[42px] overflow-x-auto overflow-y-hidden no-scrollbar flex-nowrap'
    const closedRow = 'border border-tokens-neutral-border-1 focus-within:border-[#846FE1]'
    const openRow = 'border border-[#d6d6d6] focus-within:border-[#846FE1]'
    const rowCls = `${baseRow} ${isOpen ? openRow : closedRow}`

    return (
      <div className='relative'>
        <div
          ref={attachScrollEl}
          className={`${rowCls} select-none`}
          onClick={!isOpen ? onOpen : undefined}
          style={{ scrollBehavior: 'auto' }}
        >
          <div className='w-4 h-4 shrink-0 flex items-center justify-center'>
            <SearchIcon />
          </div>

          {filterChips.map(chip => (
            <div
              key={chip.id}
              data-chip
              className='h-[30px] flex items-center gap-1 pl-2 pr-2 py-1 bg-[rgba(159,159,248,0.3)] rounded-[50px] shrink-0 select-none'
            >
              {chip.category && (
                <Typography
                  fontFamily='geistMono'
                  fontSize='labelSm'
                  fontWeight='semibold'
                  className='text-tokens-neutral-text-secondary'
                >
                  {chip.category}
                </Typography>
              )}
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                className='text-tokens-neutral-text-primary'
              >
                {chip.label}
              </Typography>
              {showSelectedAsChips && (
                <button
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => onRemoveChip(chip.id)}
                  className='flex items-center justify-center w-4 h-4 rounded'
                  aria-label='remove chip'
                >
                  <CloseIcon />
                </button>
              )}
            </div>
          ))}

          <div
            className={`${isFull ? 'w-[0.5ch] min-w-[0.5ch] shrink-0' : 'flex-1 min-w-[60px]'} relative`}
          >
            <input
              type='text'
              value={searchValue}
              onChange={onSearchChange}
              onKeyDown={onKeyDown}
              onFocus={!isOpen ? onOpen : undefined}
              placeholder=''
              className='w-full bg-transparent border-none outline-none py-1 text-sm leading-snug font-medium text-black/90 select-text'
            />
            {!searchValue && filterChips.length === 0 && (
              <div className='absolute inset-0 pointer-events-none flex items-center py-1'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMdMedium'
                  fontWeight='medium'
                  className='text-black/25 select-none'
                >
                  {placeholderText}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
)
SearchRow.displayName = 'SearchRow'
const BranchMultiSelectInner: React.FC<MultiSelectProps> = ({
  items,
  selectedItems,
  onSelectionChange,
  placeholder = 'Search or select...',
  searchPlaceholder,
  showSelectedAsChips = true,
  className = '',
  maxHeight = 'max-h-64',
  isLoading = false,
  onOpenChange
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isFull, setIsFull] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const anchorRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null)
  const [panelRect, setPanelRect] = useState<{ left: number; top: number; width: number } | null>(
    null
  )

  const modeRef = useRef<'full' | 'not'>('not')
  const consecRef = useRef(0)
  const cooldownUntilRef = useRef(0)

  const REQUIRED_FRAMES = 2
  const SWITCH_TO_FULL = 10
  const SWITCH_TO_NOT = 1
  const SWITCH_COOLDOWN_MS = 100

  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

  const raf2 = (fn: () => void) => requestAnimationFrame(() => requestAnimationFrame(fn))
  const bootRef = useRef(false)

  const bootstrapAlign = () => {
    if (bootRef.current) return
    const el = scrollRef.current
    if (!el) return
    const overflow = Math.ceil(el.scrollWidth - el.clientWidth)
    if (overflow > 1) {
      modeRef.current = 'full'
      setIsFull(true)
      el.scrollLeft = el.scrollWidth
      cooldownUntilRef.current = now() + SWITCH_COOLDOWN_MS
      bootRef.current = true
    }
  }

  const alignEndIfOverflow = () => {
    const el = scrollRef.current
    if (!el) return
    const overflow = Math.ceil(el.scrollWidth - el.clientWidth)
    if (overflow > 1) el.scrollLeft = el.scrollWidth
  }

  const measureAndMaybeSwitch = () => {
    const el = scrollRef.current
    if (!el) return
    if (now() < cooldownUntilRef.current) return

    const overflow = Math.ceil(el.scrollWidth - el.clientWidth)

    let target: 'full' | 'not' = modeRef.current
    if (modeRef.current === 'not') {
      if (overflow >= SWITCH_TO_FULL) {
        consecRef.current += 1
        if (consecRef.current >= REQUIRED_FRAMES) target = 'full'
      } else {
        consecRef.current = 0
      }
    } else {
      if (overflow <= SWITCH_TO_NOT) {
        consecRef.current += 1
        if (consecRef.current >= REQUIRED_FRAMES) target = 'not'
      } else {
        consecRef.current = 0
      }
    }

    if (target !== modeRef.current) {
      modeRef.current = target
      consecRef.current = 0
      cooldownUntilRef.current = now() + SWITCH_COOLDOWN_MS

      if (target === 'full') {
        alignEndIfOverflow()
        if (!isFull) setIsFull(true)
      } else {
        const needZero = el.scrollLeft !== 0
        if (needZero) el.scrollLeft = 0
        if (isFull) setIsFull(false)
      }
    } else {
      if (modeRef.current === 'full') alignEndIfOverflow()
    }
  }

  const roRef = useRef<ResizeObserver | null>(null)
  const moRef = useRef<MutationObserver | null>(null)

  const attachScrollEl = useCallback((node: HTMLDivElement | null) => {
    roRef.current?.disconnect()
    moRef.current?.disconnect()
    scrollRef.current = node
    if (!node) return

    raf2(bootstrapAlign)
    requestAnimationFrame(measureAndMaybeSwitch)

    if ('ResizeObserver' in window) {
      let lastW: number | null = null
      roRef.current = new ResizeObserver(([entry]) => {
        const w = entry.contentRect.width
        if (lastW !== null && Math.abs(w - lastW) < 1) return
        lastW = w
        requestAnimationFrame(measureAndMaybeSwitch)
        if (!bootRef.current) raf2(bootstrapAlign)
      })
      roRef.current.observe(node)
    }

    if ('MutationObserver' in window) {
      moRef.current = new MutationObserver(() => {
        requestAnimationFrame(measureAndMaybeSwitch)
        if (!bootRef.current) raf2(bootstrapAlign)
      })
      moRef.current.observe(node, { childList: true, subtree: false })
    }
  }, [])

  const handleBlurWithin = (e: React.FocusEvent<HTMLDivElement>) => {
    const root = containerRef.current
    const panel = panelRef.current
    const next = e.relatedTarget as Node | null
    if (!next) return
    if ((root && root.contains(next)) || (panel && panel.contains(next))) return
    setIsOpen(false)
  }

  useEffect(() => {
    onOpenChange?.(isOpen)
  }, [isOpen, onOpenChange])

  /** Close when clicking outside (capture phase) */
  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (ev: PointerEvent) => {
      const target = ev.target as Node
      const root = containerRef.current
      const panel = panelRef.current
      if ((root && root.contains(target)) || (panel && panel.contains(target))) return
      setIsOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [isOpen])

  /** Close on ESC */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  /** Re-align once after fonts load to avoid jitter caused by changing glyph widths */
  useEffect(() => {
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        raf2(bootstrapAlign)
        requestAnimationFrame(measureAndMaybeSwitch)
      })
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => raf2(bootstrapAlign), 0)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    let host = document.getElementById('ms-portal-root') as HTMLElement | null
    if (!host) {
      host = document.createElement('div')
      host.id = 'ms-portal-root'
      document.body.appendChild(host)
    }
    setPortalHost(host)
  }, [])

  const getScrollParents = (el: HTMLElement | null): HTMLElement[] => {
    const parents: HTMLElement[] = []
    let node = el?.parentElement
    while (node && node !== document.body) {
      const style = getComputedStyle(node)
      const overflowY = style.overflowY
      const overflowX = style.overflowX
      const scrollable =
        /(auto|scroll|overlay)/.test(overflowY) || /(auto|scroll|overlay)/.test(overflowX)
      if (scrollable) parents.push(node)
      node = node.parentElement
    }
    parents.push((document.scrollingElement as HTMLElement) || document.documentElement)
    return parents
  }

  const updatePanelPosition = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const r = anchor.getBoundingClientRect()
    setPanelRect({
      left: Math.round(r.left),
      top: Math.round(r.top),
      width: Math.round(r.width)
    })
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) return

    updatePanelPosition()

    const anchor = anchorRef.current
    const parents = getScrollParents(anchor || null)

    const onResize = () => updatePanelPosition()

    const onParentScroll = (ev: Event) => {
      const panel = panelRef.current
      if (panel && panel.contains(ev.target as Node)) {
        return
      }
      setIsOpen(false)
    }

    window.addEventListener('resize', onResize, { passive: true })
    parents.forEach(p => p.addEventListener('scroll', onParentScroll, { passive: true }))

    const io = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (!entry?.isIntersecting) setIsOpen(false)
      },
      { root: null, threshold: 0.05 }
    )
    if (anchor) io.observe(anchor)

    return () => {
      window.removeEventListener('resize', onResize)
      parents.forEach(p => p.removeEventListener('scroll', onParentScroll))
      io.disconnect()
    }
  }, [isOpen, updatePanelPosition])

  useLayoutEffect(() => {
    if (isOpen) {
      const input = scrollRef.current?.querySelector('input') as HTMLInputElement | null
      try {
        input?.focus?.({ preventScroll: true })
      } catch {
        input?.focus?.()
      }
    }
  }, [isOpen])

  const filteredItems = items.filter(
    i =>
      i.label.toLowerCase().includes(searchValue.toLowerCase()) ||
      i.value.toLowerCase().includes(searchValue.toLowerCase())
  )

  const filterChips = showSelectedAsChips
    ? (selectedItems
        .map(id => {
          const it = items.find(x => x.id === id)
          if (!it) return null
          return {
            id,
            label: it.label.length > 20 ? `...${it.label.slice(-17)}` : it.label,
            value: it.value,
            category: it.category
          }
        })
        .filter(Boolean) as { id: string; label: string; value: string; category?: string }[])
    : []

  useLayoutEffect(() => {
    measureAndMaybeSwitch()
    if (filterChips.length > 0 && !bootRef.current) raf2(bootstrapAlign)
  }, [filterChips.length])

  useLayoutEffect(() => {
    measureAndMaybeSwitch()
    if (!bootRef.current) raf2(bootstrapAlign)
  }, [isOpen])

  /** Interaction handlers */
  const handleItemClick = (itemId: string) => {
    if (selectedItems.includes(itemId)) {
      onSelectionChange(selectedItems.filter(id => id !== itemId))
    } else {
      onSelectionChange([...selectedItems, itemId])
    }
    requestAnimationFrame(measureAndMaybeSwitch)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
    requestAnimationFrame(measureAndMaybeSwitch)
  }

  const handleFilterChipRemove = (chipId: string) => {
    onSelectionChange(selectedItems.filter(id => id !== chipId))
    requestAnimationFrame(measureAndMaybeSwitch)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (e.key === 'Backspace' || e.key === 'Delete') &&
      searchValue === '' &&
      selectedItems.length > 0
    ) {
      e.preventDefault()
      const last = selectedItems[selectedItems.length - 1]
      handleFilterChipRemove(last)
    }
  }

  const placeholderText =
    filterChips.length > 0 ? '' : searchPlaceholder || placeholder || 'Search or select...'

  return (
    <div
      ref={containerRef}
      onBlurCapture={handleBlurWithin}
      className={`relative w-full ${className}`}
    >
      <div className='bg-white px-4 py-2' ref={anchorRef}>
        <div className={isOpen ? 'invisible pointer-events-none' : ''}>
          <SearchRow
            isOpen={isOpen}
            isFull={isFull}
            filterChips={filterChips}
            showSelectedAsChips={showSelectedAsChips}
            searchValue={searchValue}
            placeholderText={placeholderText}
            attachScrollEl={attachScrollEl}
            onOpen={() => setIsOpen(true)}
            onSearchChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onRemoveChip={handleFilterChipRemove}
          />
        </div>
      </div>
      {isOpen &&
        portalHost &&
        panelRect &&
        createPortal(
          <div
            ref={panelRef}
            className='fixed z-[10050]'
            style={{
              left: 0,
              top: 0,
              width: panelRect.width,
              transform: `translate(${panelRect.left}px, ${panelRect.top}px)`
            }}
          >
            <div className='bg-white rounded-[16px] overflow-hidden shadow-1'>
              <div className='px-4 pt-2'>
                <SearchRow
                  isOpen={isOpen}
                  isFull={isFull}
                  filterChips={filterChips}
                  showSelectedAsChips={showSelectedAsChips}
                  searchValue={searchValue}
                  placeholderText={placeholderText}
                  attachScrollEl={attachScrollEl}
                  onOpen={() => setIsOpen(true)}
                  onSearchChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  onRemoveChip={handleFilterChipRemove}
                />
              </div>

              <div className='pr-1 pb-3 pt-2 pl-4'>
                <div className={`${maxHeight} overflow-y-auto space-y-3`}>
                  {filteredItems.map(item => {
                    const selected = selectedItems.includes(item.id)
                    return (
                      <div
                        key={item.id}
                        className='flex items-center h-8 cursor-pointer'
                        onMouseDown={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleItemClick(item.id)
                        }}
                        role='option'
                        aria-selected={selected}
                      >
                        <div className='w-8 h-8 flex items-center justify-center'>
                          <div className='w-4 h-4 flex items-center justify-center'>
                            {selected && (
                              <div className='w-full h-full flex items-center justify-center'>
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='16'
                                  height='16'
                                  viewBox='0 0 16 16'
                                  fill='none'
                                >
                                  <path
                                    d='M14.2503 2.96875H13.1581C13.0049 2.96875 12.8596 3.03906 12.7659 3.15937L6.32369 11.3203L3.23463 7.40625C3.1879 7.34692 3.12833 7.29895 3.06041 7.26593C2.99249 7.23292 2.91796 7.21572 2.84244 7.21562H1.75025C1.64557 7.21562 1.58775 7.33594 1.65182 7.41719L5.9315 12.8391C6.1315 13.0922 6.51588 13.0922 6.71744 12.8391L14.3487 3.16875C14.4128 3.08906 14.3549 2.96875 14.2503 2.96875Z'
                                    fill='#65558F'
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>

                        <Typography
                          fontFamily='geist'
                          fontSize='bodyMd'
                          fontWeight='semibold'
                          className={`flex-1 select-none ${selected ? 'text-tokens-primary-2' : 'text-tokens-neutral-text-primary'}`}
                        >
                          {item.label}
                        </Typography>
                      </div>
                    )
                  })}

                  {isLoading && (
                    <div className='flex items-center justify-center h-16'>
                      <LoadingSpinner clipPathId='branch-loading' />
                    </div>
                  )}

                  {!isLoading && filteredItems.length === 0 && (
                    <div className='flex items-center justify-center h-16'>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodyMd'
                        className='text-tokens-neutral-text-secondary select-none'
                      >
                        No items found
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          portalHost
        )}
    </div>
  )
}

export const BranchMultiSelect = BranchMultiSelectInner
export default React.memo(BranchMultiSelectInner)
